import React, {useCallback, useEffect, useRef, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import ConsentController from "@source/api/controllers/consentController";
import ReqError from "@source/api/reqError";
import {createDeclineConsentRequest, createEmployeeSignRequest, getConsentSignerRoleLabel} from "@source/api/models/consentHelpers";
import {ConsentCategorySource, ConsentDocument, ConsentDocumentActiveStage, ConsentDocumentStatus, CONSENT_AGREE_STATEMENT, CONSENT_DECLINE_STATEMENT} from "@source/api/models/consentModels";
import Button from "@source/components/UI/buttons/button/button";
import Checkbox from "@source/components/UI/buttons/checkbox/checkbox";
import CacheImg from "@source/components/UI/cacheImg/cacheImg";
import userStore from "@source/store/userStore";
import {HOME_ROUTES, stringToHslColor} from "@source/utils/utils";
import {ConsentDocumentPreview} from "@source/components/consentDocuments/consentDocumentPreview";
import {formatConsentDate} from "@source/components/consentDocuments/consentDate";
import {ConsentRequestState, SignatoryProgress} from "@source/components/consentDocuments";
import {canCurrentEmployeeSign, employeeSignAccessFromUser, employeeSignAttemptEnded, getEmployeeSignErrorMessage, getEmployeeSignSuccessTitle, isEmployeeRoleSignature} from "@source/components/consentDocuments/employeeSigningHelpers";
import StepBackBtn from "@source/pages/HomePage/homeRouter/updateTab/steps/step/stepBackBtn/stepBackBtn";
import styles from "./employeeSigning.module.scss";

export const EmployeeSigning = () => {
  const {documentId} = useParams<{documentId: string}>();
  const history = useHistory();
  const [document, setDocument] = useState<ConsentDocument>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [submitting, setSubmitting] = useState<"sign" | "decline" | null>(null);
  const [result, setResult] = useState<"completed" | "waiting" | "declined">();
  const [unavailable, setUnavailable] = useState("");
  const [terminalFailure, setTerminalFailure] = useState<403 | 404 | 409>();
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);
  const employeeId = userStore.getId() || "";

  const load = useCallback(async () => {
    if (!documentId) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    setUnavailable("");
    setTerminalFailure(undefined);
    setResult(undefined);
    setAcknowledged(false);
    setPreviewReady(false);
    try {
      const response = await ConsentController.getEmployeeDocument(documentId);
      if (mountedRef.current && requestId === requestIdRef.current) setDocument(response);
    } catch (requestError: unknown) {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setError(getEmployeeSignErrorMessage(requestError));
      }
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
    };
  }, [load]);

  const signAccess = employeeSignAccessFromUser(userStore.getUserInfo(), true);
  const isActiveAssignee = document
    ? canCurrentEmployeeSign(document, employeeId, signAccess)
    : false;
  const signingAsEmployeeRole = document ? isEmployeeRoleSignature(document, signAccess) : false;
  const isClinicalSigner = !signingAsEmployeeRole
    && document?.clinicalSigner?._id === employeeId
    && Array.isArray(document.activeStages)
    && document.activeStages.includes(ConsentDocumentActiveStage.AWAITING_CLINICAL_SIGNATURE);
  const isFinalizing = Boolean(document
    && document.status === ConsentDocumentStatus.AWAITING_SIGNATURES
    && Array.isArray(document.activeStages)
    && document.activeStages.length === 0);
  const fail = (requestError: unknown) => {
    if (!mountedRef.current) return;
    const status = requestError instanceof ReqError ? requestError.status : undefined;
    if (employeeSignAttemptEnded(requestError)) {
      setAcknowledged(false);
      setPreviewReady(false);
      setTerminalFailure(status === 403 || status === 404 ? status : 409);
    }
    setUnavailable(getEmployeeSignErrorMessage(requestError));
  };
  const sign = async () => {
    if (!document || !documentId || !acknowledged || !previewReady || !isActiveAssignee || submitting || terminalFailure) return;
    setSubmitting("sign");
    setUnavailable("");
    try {
      const response = await ConsentController.signEmployeeDocument(documentId, createEmployeeSignRequest(isClinicalSigner));
      if (!mountedRef.current) return;
      setDocument(response);
      setResult(response.status === ConsentDocumentStatus.COMPLETED ? "completed" : "waiting");
    } catch (requestError: unknown) {
      fail(requestError);
    } finally {
      if (mountedRef.current) setSubmitting(null);
    }
  };
  const decline = async () => {
    if (!documentId || !previewReady || !isActiveAssignee || submitting || terminalFailure) return;
    setSubmitting("decline");
    setUnavailable("");
    try {
      const response = await ConsentController.declineEmployeeDocument(documentId, createDeclineConsentRequest());
      if (!mountedRef.current) return;
      setDocument(response);
      setResult("declined");
    } catch (requestError: unknown) {
      fail(requestError);
    } finally {
      if (mountedRef.current) setSubmitting(null);
    }
  };

  if (loading) return <ConsentRequestState variant="loading" title="Loading form"/>;
  if (error || !document) return <ConsentRequestState variant="error" title="Form unavailable" description={error || "This form is no longer available."} onRetry={() => void load()}/>;
  if (result) return <div className={styles.result}>
    <h2>{result === "declined" ? "Consent to form declined" : getEmployeeSignSuccessTitle(document.status)}</h2>
    <p>{result === "declined" ? "Consent to form was not given. This form is now closed." : result === "completed" ? "All required form steps are complete." : "Your signature was recorded successfully."}</p>
    <Button event="link" link={HOME_ROUTES.ConsentDocumentsAwaitingYourSign}>Return to Awaiting Your Signature</Button>
  </div>;
  if (terminalFailure) return <div className={styles.result}>
    <h2>{terminalFailure === 409 ? "Signing no longer available" : "Signing unavailable"}</h2>
    <p>{unavailable}</p>
    {terminalFailure === 409 ? <Button variant="outlined" onClick={() => void load()}>Refresh form</Button> : null}
    <Button event="link" link={HOME_ROUTES.ConsentDocumentsAwaitingYourSign}>Return to Awaiting Your Signature</Button>
  </div>;

  const residentName = [document.resident.firstName, document.resident.lastName].filter(Boolean).join(" ") || "Resident unavailable";
  const residentInitials = `${document.resident.firstName?.[0] || ""}${document.resident.lastName?.[0] || ""}`.toUpperCase() || "R";
  const categoryName = document.categorySnapshot?.name || "Legacy form";
  const source = document.categorySnapshot?.source === ConsentCategorySource.RAW_PDF || document.source === ConsentCategorySource.RAW_PDF ? "Existing PDF" : "Template";
  const signingOrder = document.categorySnapshot?.signingOrderRequired
    ? (document.categorySnapshot.signingOrder || []).map((role) => getConsentSignerRoleLabel(role)).join(" → ") || "Signing order unavailable"
    : "Required signer roles may sign in parallel.";

  return <div className={styles.signing}>
    <section className={styles.details}>
      <div className={styles.residentSummary}>
        <CacheImg url={document.resident.photo} alt={`${residentName} avatar`} className={styles.photo} imgClassName={styles.photo} skeletonProps={{variant: "circular"}}>
          <div className={styles.noPhoto} style={{backgroundColor: stringToHslColor(`${document.resident.firstName || ""}${document.resident.lastName || ""}`)}}>{residentInitials}</div>
        </CacheImg>
        <div><h2>{residentName}</h2><p>{document.title || "Untitled form"}</p></div>
      </div>
      <StepBackBtn onClick={() => history.push(HOME_ROUTES.ConsentDocumentsAwaitingYourSign)} label="Back to forms" disabled={Boolean(submitting)}/>
      <dl>
        <div><dt>Form Category</dt><dd>{categoryName}</dd></div>
        <div><dt>Source</dt><dd>{source}</dd></div>
        <div><dt>Sent</dt><dd>{formatConsentDate(document.sentAt)}</dd></div>
        <div><dt>Nursing home</dt><dd>{document.nursingHome?.name || "Nursing home unavailable"}</dd></div>
        <div><dt>Signing order</dt><dd>{signingOrder}</dd></div>
      </dl>
      <section className={styles.progress}><h3>Signing progress</h3><SignatoryProgress compact signatories={document.signatories} employeeSignerRoles={document.categorySnapshot?.employeeSignerRoles}/></section>
    </section>
    <section className={styles.review}>
      <h2>{isClinicalSigner ? "GP / Clinician signature requested" : "Review form"}</h2>
      <ConsentDocumentPreview className={styles.reviewPreview} fill documentId={document._id} onReadinessChange={setPreviewReady}/>
      {unavailable ? <div className={styles.submitError} role="alert">{unavailable}</div> : null}
      {isActiveAssignee ? <div className={styles.actions}>
        <Checkbox name="employee-consent-acknowledgement" value="confirmed" checked={acknowledged} disabled={Boolean(submitting) || !previewReady} onChange={(event) => setAcknowledged(event.target.checked)} label={CONSENT_AGREE_STATEMENT}/>
        <Button className={styles.agreeButton} onClick={() => void sign()} disabled={!acknowledged || !previewReady || Boolean(submitting)} isLoading={submitting === "sign"}>{CONSENT_AGREE_STATEMENT}</Button>
        <Button className={styles.declineButton} onClick={() => void decline()} disabled={!previewReady || Boolean(submitting)} isLoading={submitting === "decline"}>{CONSENT_DECLINE_STATEMENT}</Button>
      </div> : <ConsentRequestState
        variant="empty"
        title={isFinalizing ? "Form finalizing" : "Signing unavailable"}
        description={isFinalizing
          ? "This form is finalizing. Return to Awaiting Your Signature and refresh for its current state."
          : "You are not an active employee signer for this document."}
      />}
    </section>
  </div>;
};
